import React, { memo, useMemo } from 'react';
import { Trash2, User, Bed, Calendar } from 'lucide-react';
import { Room } from '../../../types/accommodation';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getAgeColorClass } from '../../../utils/ageUtils';
import { motion } from 'framer-motion';

type Props = {
  room: Room;
  onViewRoom: (roomNumber: number) => void;
  onRemoveRoom?: (roomNumber: number) => Promise<void>;
  canRemove?: boolean;
};

export const RoomTableRow = memo(({ room, onViewRoom, onRemoveRoom, canRemove = false }: Props) => {
  const { translations, direction } = useLanguage();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  
  // Check if user can view full details of this assignment
  const canViewFullDetails = (assignment: any): boolean => {
    if (userRole === 'admin') return true;
    if (userRole === 'guide' && userId === assignment.gid) return true;
    if (userRole === 'organizer' && userId === assignment.oid) return true;
    return false;
  };

  // Mask sensitive information
  const maskText = (text: string) => '••••';

  const getRoomTypeLabel = (type: string): string => {
    switch (type) {
      case 'd': return translations?.roomTableRow?.roomTypes?.double || 'Double';
      case 't': return translations?.roomTableRow?.roomTypes?.triple || 'Triple';
      case 'q': return translations?.roomTableRow?.roomTypes?.quadruple || 'Quadruple';
      default: return type;
    }
  };

  const getOccupancyStatus = () => {
    if (room.o === 0) return 'available';
    if (room.o === room.c) return 'occupied';
    return 'partial';
  };

  const getStatusBadgeStyles = () => {
    switch (status) {
      case 'occupied':
        return 'bg-red-50 text-red-700';
      case 'partial':
        return 'bg-yellow-50 text-yellow-700';
      default:
        return 'bg-green-50 text-green-700';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'occupied':
        return translations?.roomTableRow?.statuses?.full || 'Full';
      case 'partial':
        return translations?.roomTableRow?.statuses?.partial || 'Partially Occupied';
      default:
        return translations?.roomTableRow?.statuses?.available || 'Available';
    }
  };

  const status = getOccupancyStatus();
  
  // Format date for display - updating to show only day/month format
  const formatDate = (dateStr: string) => {
    // For DD/MM/YYYY format
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const [day, month] = dateStr.split('/').map(Number);
      return `${day}/${month}`;
    }
    
    // For timestamp format
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      // Return just day/month without leading zeros on month
      const day = date.getDate();
      const month = date.getMonth() + 1;
      return `${day}/${month}`;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to check if two date ranges overlap - updated for string dates
  const checkOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
    // Convert DD/MM/YYYY to Date objects for comparison
    const parseDate = (dateStr: string) => {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };
    
    const s1 = parseDate(start1);
    const e1 = parseDate(end1);
    const s2 = parseDate(start2);
    const e2 = parseDate(end2);
    
    // Check for overlap - returns true if periods overlap
    return s1 < e2 && s2 < e1;
  };

  // Group all assignments by periods, merging overlapping ones
  const periodsWithAssignments = useMemo(() => {
    // Collect all assignments across all beds
    const allAssignments: {
      assignment: any;
      bedIndex: number;
    }[] = [];
    
    room.b.forEach((bed, bedIndex) => {
      if (bed.a && bed.a.length > 0) {
        bed.a.forEach(assignment => {
          allAssignments.push({ assignment, bedIndex });
        });
      }
    });
    
    // Sort assignments by check-in date
    allAssignments.sort((a, b) => {
      // Convert string dates to Date objects for sorting
      const [aDay, aMonth, aYear] = a.assignment.ci.split('/').map(Number);
      const [bDay, bMonth, bYear] = b.assignment.ci.split('/').map(Number);
      
      const dateA = new Date(aYear, aMonth - 1, aDay);
      const dateB = new Date(bYear, bMonth - 1, bDay);
      
      return dateA.getTime() - dateB.getTime();
    });
    
    // Group assignments into merged periods
    const mergedPeriods: {
      startDate: string;
      endDate: string;
      assignments: {
        assignment: any;
        bedIndex: number;
      }[];
    }[] = [];
    
    // Process each assignment
    allAssignments.forEach(assignmentData => {
      const { assignment, bedIndex } = assignmentData;
      const start = assignment.ci;
      const end = assignment.co;
      
      // Try to find an existing period that overlaps
      let overlappingPeriodIndices: number[] = [];
      
      mergedPeriods.forEach((period, index) => {
        if (checkOverlap(start, end, period.startDate, period.endDate)) {
          overlappingPeriodIndices.push(index);
        }
      });
      
      // If no overlap, create a new period
      if (overlappingPeriodIndices.length === 0) {
        mergedPeriods.push({
          startDate: start,
          endDate: end,
          assignments: [{ assignment, bedIndex }]
        });
      } else {
        // Merge all overlapping periods
        const firstPeriodIndex = overlappingPeriodIndices[0];
        const firstPeriod = mergedPeriods[firstPeriodIndex];
        
        // Collect all assignments from overlapping periods
        const allOverlappingAssignments = [...firstPeriod.assignments];
        
        // Add the current assignment
        allOverlappingAssignments.push({ assignment, bedIndex });
        
        // Determine the new date range
        // Convert dates to compare them
        const parseDate = (dateStr: string) => {
          const [day, month, year] = dateStr.split('/').map(Number);
          return new Date(year, month - 1, day);
        };
        
        // Find earliest start date and latest end date
        let earliestStart = parseDate(start);
        let latestEnd = parseDate(end);
        
        if (parseDate(firstPeriod.startDate) < earliestStart) {
          earliestStart = parseDate(firstPeriod.startDate);
        }
        
        if (parseDate(firstPeriod.endDate) > latestEnd) {
          latestEnd = parseDate(firstPeriod.endDate);
        }
        
        // Format dates back to DD/MM/YYYY
        const formatBackToString = (date: Date) => {
          return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
        };
        
        // Process additional overlapping periods (if more than one)
        if (overlappingPeriodIndices.length > 1) {
          // Skip the first one as we already processed it
          for (let i = 1; i < overlappingPeriodIndices.length; i++) {
            const periodIndex = overlappingPeriodIndices[i];
            const period = mergedPeriods[periodIndex];
            
            // Add assignments from this period
            allOverlappingAssignments.push(...period.assignments);
            
            // Update date range
            const periodStart = parseDate(period.startDate);
            const periodEnd = parseDate(period.endDate);
            
            if (periodStart < earliestStart) {
              earliestStart = periodStart;
            }
            
            if (periodEnd > latestEnd) {
              latestEnd = periodEnd;
            }
          }
        }
        
        // Update the first period with merged data
        mergedPeriods[firstPeriodIndex] = {
          startDate: formatBackToString(earliestStart),
          endDate: formatBackToString(latestEnd),
          assignments: allOverlappingAssignments
        };
        
        // Remove the other overlapping periods (in reverse order to avoid index shifts)
        for (let i = overlappingPeriodIndices.length - 1; i > 0; i--) {
          mergedPeriods.splice(overlappingPeriodIndices[i], 1);
        }
      }
    });
    
    // Sort merged periods by start date
    return mergedPeriods.sort((a, b) => {
      const [aDay, aMonth, aYear] = a.startDate.split('/').map(Number);
      const [bDay, bMonth, bYear] = b.startDate.split('/').map(Number);
      
      const dateA = new Date(aYear, aMonth - 1, aDay);
      const dateB = new Date(bYear, bMonth - 1, bDay);
      
      return dateA.getTime() - dateB.getTime();
    });
  }, [room.b]);

  // Get all beds with assignments 
  const allBedsWithAssignments = useMemo(() => {
    // Get unique bed indices that have assignments
    const bedIndices = new Set<number>();
    periodsWithAssignments.forEach(period => {
      period.assignments.forEach(({ bedIndex }) => {
        bedIndices.add(bedIndex);
      });
    });
    
    // Return sorted array of bed indices
    return Array.from(bedIndices).sort((a, b) => a - b);
  }, [periodsWithAssignments]);

  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onViewRoom(room.n)}
    >
      {/* Room Number - Now clickable */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <motion.span 
            whileHover={{ scale: 1.05 }}
            className="text-lg font-semibold text-gray-900 text-primary-600 underline underline-offset-2 decoration-dotted"
          >
            {room.n}
          </motion.span>
          <span className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full ${getStatusBadgeStyles()}`}>
            <span className="text-xs font-medium">
              {getStatusLabel()}
            </span>
          </span>
        </div>
      </td>

      {/* Room Type */}
      <td className="px-6 py-4">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-primary-50 text-primary-700">
          {getRoomTypeLabel(room.t)}
        </span>
      </td>

      {/* Capacity & Occupancy */}
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {translations?.roomTableRow?.capacity || 'Capacity'}:
            </span>
            <span className="text-sm font-medium">
              {room.o} / {room.c}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-1.5 rounded-full transition-all ${
                status === 'occupied'
                  ? 'bg-red-500'
                  : status === 'partial'
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min((room.o / room.c) * 100, 100)}%` }}
            />
          </div>
        </div>
      </td>

      {/* Periods - Horizontal Layout */}
      <td className="px-6 py-4">
        {periodsWithAssignments.length > 0 ? (
          <div className="w-full overflow-x-auto" style={{ width: '600px' }}>
            {/* Horizontal Periods Layout */}
            <div className="min-w-full">
              {/* Periods Header Row */}
              <div className="flex border-b">
                {/* Empty cell for bed labels */}
                <div className="w-24 shrink-0 p-2 font-medium text-gray-700 bg-gray-50 border-r">
                  {translations?.roomTableRow?.beds || 'Beds'}
                </div>
                
                {/* Period Headers */}
                {periodsWithAssignments.map((period, periodIndex) => (
                  <div 
                    key={`period-header-${periodIndex}`} 
                    className="w-48 shrink-0 p-2 border-r bg-primary-50"
                  >
                    <div className="text-center">
                      <div className="font-medium text-primary-700 flex items-center justify-center gap-1">
                        <Calendar size={14} className="text-primary-600" />
                        <span>Period {periodIndex + 1}</span>
                      </div>
                      <div className="text-sm text-primary-600 mt-1">
                        {direction === 'rtl'
                          ? `${formatDate(period.endDate)} ← ${formatDate(period.startDate)}`
                          : `${formatDate(period.startDate)} → ${formatDate(period.endDate)}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Beds and Assignments Grid */}
              <div className="border-b">
                {allBedsWithAssignments.map(bedIndex => (
                  <div className="flex" key={`bed-row-${bedIndex}`}>
                    {/* Bed Label */}
                    <div className="w-24 shrink-0 p-2 border-r bg-gray-50 flex items-center">
                      <div className="bg-white px-2 py-1 rounded-full text-xs font-medium border border-gray-200 flex items-center gap-1">
                        <Bed size={12} />
                        <span>Bed {bedIndex + 1}</span>
                      </div>
                    </div>
                    
                    {/* Assignment Cells for Each Period */}
                    {periodsWithAssignments.map((period, periodIndex) => {
                      // Find assignment(s) for this bed in this period
                      const bedAssignments = period.assignments.filter(a => a.bedIndex === bedIndex);
                      
                      if (bedAssignments.length > 0) {
                        // Show most recent assignment first
                        const mostRecentAssignment = bedAssignments.sort((a, b) => {
                          // Sort by date - convert string dates to Date objects
                          const [aDay, aMonth, aYear] = a.assignment.ci.split('/').map(Number);
                          const [bDay, bMonth, bYear] = b.assignment.ci.split('/').map(Number);
                          
                          const dateA = new Date(aYear, aMonth - 1, aDay);
                          const dateB = new Date(bYear, bMonth - 1, bDay);
                          
                          return dateB.getTime() - dateA.getTime();
                        })[0].assignment;
                        
                        return (
                          <div 
                            key={`cell-${bedIndex}-${periodIndex}`} 
                            className={`w-48 shrink-0 p-2 border-r ${
                              mostRecentAssignment.isStaff ? 'bg-gray-50' : 
                              mostRecentAssignment.g === 'F' ? 'bg-pink-50/70' : 'bg-blue-50/70'
                            }`}
                          >
                            <div className="flex flex-col gap-1">
                              {bedAssignments.map((assignmentData, idx) => {
                                const { assignment } = assignmentData;

                                const isExactPeriod =
                                  assignment.ci === period.startDate && 
                                  assignment.co === period.endDate;

                                return (
                                  <div 
                                    key={`assignment-${bedIndex}-${periodIndex}-${idx}`}
                                    className="flex items-start gap-2 border rounded-md p-1 mb-1 border-gray-300 bg-white shadow-sm"
                                  >
                                    <div
                                      className={`p-1 rounded-full ${
                                        assignment.isStaff ? 'bg-gray-200 text-gray-600' :
                                        assignment.g === 'F' ? 'bg-pink-100' : 'bg-blue-100'
                                      } shrink-0`}
                                    >
                                      <User
                                        size={14}
                                        className={
                                          assignment.isStaff ? 'text-gray-600' :
                                          assignment.g === 'F' ? 'text-pink-500' : 'text-blue-500'
                                        }
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="font-medium text-sm truncate">
                                        {canViewFullDetails(assignment) ? assignment.pn : maskText(assignment.pn)}
                                      </div>
                                      <div className={`text-[10px] mt-1 flex items-center gap-2 flex-wrap px-1 py-0.5 rounded-md ${
                                        isExactPeriod
                                          ? 'text-gray-500'
                                          : 'text-red-700 bg-red-50 '
                                      }`}>
                                        <span>{formatDate(assignment.ci)} → {formatDate(assignment.co)}</span>
                                        {assignment.age && (
                                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] leading-none text-white ${getAgeColorClass(assignment.age)}`}>
                                            {assignment.age}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      
                      // Empty cell (no assignment for this bed in this period)
                      return (
                        <div 
                          key={`cell-${bedIndex}-${periodIndex}`} 
                          className="w-48 shrink-0 p-2 border-r bg-gray-50/30"
                        >
                          <div className="flex items-center justify-center h-full text-xs text-gray-400">
                            -
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Empty beds section */}
            {room.b.some(bed => !bed.a || bed.a.length === 0) && (
              <div className="mt-4 border rounded-lg">
                <div className="bg-gray-50 px-3 py-2 border-b">
                  <div className="font-medium text-gray-600">
                    {translations?.roomTableRow?.availableBeds || 'Available Beds'}
                  </div>
                </div>
                <div className="p-2 flex flex-wrap gap-2">
                  {room.b.map((bed, bedIndex) => {
                    if (!bed.a || bed.a.length === 0) {
                      return (
                        <div key={`empty-${bedIndex}`} className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2">
                          <div className="bg-white px-2 py-1 rounded-full text-xs font-medium border border-gray-200 flex items-center gap-1">
                            <Bed size={12} />
                            <span>Bed {bedIndex + 1}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            {translations?.roomTableRow?.noAssignments || 'No assignments for this room'}
          </div>
        )}
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // Implement deep comparison for the room prop
  // Return true if props are equal (component should NOT re-render)
  return (
    prevProps.room.n === nextProps.room.n &&
    prevProps.room.t === nextProps.room.t &&
    prevProps.room.c === nextProps.room.c &&
    prevProps.room.o === nextProps.room.o &&
    prevProps.canRemove === nextProps.canRemove
  );
});